import { supabase } from '../../../shared/lib/supabase';
import { videosService } from '../../../shared/services/videosService';
import { videoHighlightsService, VideoHighlight } from '../../../shared/services/videoHighlightsService';
import { tacticsImagesService } from '../../../shared/services/tacticsImagesService';
import { videoProcessingJobsService } from '../../../shared/services/videoProcessingJobsService';

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface DeleteR2FilesResponse {
  success: boolean;
  results: Array<{
    path: string;
    success: boolean;
    error?: string;
  }>;
  summary: {
    total: number;
    succeeded: number;
    failed: number;
  };
}

/**
 * Service for deleting files from R2 storage and updating database records
 */
export const r2DeletionService = {
  /**
   * Delete video assets (master and/or thumbnails)
   */
  async deleteVideoAssets(
    videoId: string,
    options: {
      deleteMaster: boolean;
      deleteThumbnails: boolean;
    }
  ): Promise<ServiceResult<{ deletedMaster: boolean; deletedThumbnails: boolean }>> {
    try {
      // Get current session for auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Not authenticated' };
      }

      // Fetch video record
      const videoResult = await videosService.getVideoById(videoId);
      if (!videoResult.success || !videoResult.data) {
        return { success: false, error: videoResult.error || 'Video not found' };
      }

      const video = videoResult.data;
      const orgId = video.org_id;
      const userId = session.user.id;

      // Build array of file paths to delete
      const filePaths: string[] = [];

      // Add master file path if deleting master
      if (options.deleteMaster && video.storage_path) {
        // Extract the actual file path from storage_path
        // storage_path might be a folder path, we need to find the actual file
        // For now, assume storage_path is the file path or contains the file
        filePaths.push(video.storage_path);
      }

      // Add thumbnail paths if deleting thumbnails
      if (options.deleteThumbnails) {
        const jobsResult = await videoProcessingJobsService.getJobsByVideoId(videoId);
        if (jobsResult.success && jobsResult.data) {
          const thumbnailJobs = jobsResult.data.filter(
            (job) => job.job_type === 'thumbnail' && job.status === 'succeeded' && job.is_active
          );

          for (const job of thumbnailJobs) {
            const generatedAssets = Array.isArray(job.payload?.generatedAssets)
              ? job.payload.generatedAssets
              : [];

            for (const asset of generatedAssets) {
              if (asset?.storagePath && typeof asset.storagePath === 'string') {
                filePaths.push(asset.storagePath);
              }
            }
          }
        }
      }

      if (filePaths.length === 0) {
        return { success: false, error: 'No files found to delete' };
      }

      // Call edge function to delete files from R2
      const { data: deleteResult, error: deleteError } = await supabase.functions.invoke(
        'delete-r2-files',
        {
          body: {
            orgId,
            userId,
            filePaths,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      ) as { data: DeleteR2FilesResponse | null; error: any };

      if (deleteError) {
        console.error('Error calling delete-r2-files:', deleteError);
        return { success: false, error: deleteError.message || 'Failed to delete files from R2' };
      }

      if (!deleteResult) {
        return { success: false, error: 'No response from delete-r2-files function' };
      }

      // Check if all deletions succeeded
      const allSucceeded = deleteResult.success && deleteResult.summary.failed === 0;
      if (!allSucceeded) {
        const failedPaths = deleteResult.results
          .filter((r) => !r.success)
          .map((r) => r.path);
        return {
          success: false,
          error: `Some files could not be deleted: ${failedPaths.join(', ')}`,
        };
      }

      // All R2 deletions succeeded, now update database
      // Build update payload for UpdateVideoInput
      const updatePayload: {
        size_bytes?: number;
        is_active?: boolean;
      } = {};

      // Determine if we should set is_active = false
      // First check if there are active social media uploads
      const { socialMediaService } = await import('../../../shared/services/socialMediaService');
      const hasUploadsResult = await socialMediaService.hasActiveUploads(videoId, 'video');
      const hasActiveUploads = hasUploadsResult.success && hasUploadsResult.data === true;

      let shouldSetInactive = false;

      if (options.deleteMaster && options.deleteThumbnails) {
        // Don't set is_active = false if there are active uploads
        shouldSetInactive = !hasActiveUploads;
      } else if (options.deleteMaster) {
        // Check if thumbnails exist
        const jobsResult = await videoProcessingJobsService.getJobsByVideoId(videoId);
        const hasThumbnails = jobsResult.success && jobsResult.data
          ? jobsResult.data.some(
              (job) => job.job_type === 'thumbnail' && job.status === 'succeeded' && job.is_active
            )
          : false;

        // Don't set is_active = false if thumbnails exist OR if there are active uploads
        if (!hasThumbnails && !hasActiveUploads) {
          shouldSetInactive = true;
        }
      }

      if (options.deleteMaster) {
        updatePayload.size_bytes = 0;
      }

      if (shouldSetInactive) {
        updatePayload.is_active = false;
      }

      // Update video record via service (for fields in UpdateVideoInput)
      if (Object.keys(updatePayload).length > 0) {
        const updateResult = await videosService.updateVideo(videoId, updatePayload);
        if (!updateResult.success) {
          console.error('Error updating video after deletion:', updateResult.error);
          // Note: R2 files are already deleted, but DB update failed
          // This is a partial failure scenario
          return {
            success: false,
            error: `Files deleted from R2 but database update failed: ${updateResult.error}`,
          };
        }
      }

      // Update storage_path and public_url directly via Supabase (not in UpdateVideoInput)
      if (options.deleteMaster) {
        await supabase
          .from('videos')
          .update({
            storage_path: null,
            public_url: null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', videoId);
      }

      // Mark thumbnail jobs as inactive and clear generatedAssets if deleting thumbnails
      if (options.deleteThumbnails) {
        const jobsResult = await videoProcessingJobsService.getJobsByVideoId(videoId);
        if (jobsResult.success && jobsResult.data) {
          const thumbnailJobs = jobsResult.data.filter(
            (job) => job.job_type === 'thumbnail' && job.is_active
          );

          for (const job of thumbnailJobs) {
            // Get current payload and clear generatedAssets
            const currentPayload = typeof job.payload === 'string' 
              ? JSON.parse(job.payload) 
              : (job.payload || {});
            
            const updatedPayload = {
              ...currentPayload,
              generatedAssets: [], // Clear all thumbnail assets
            };

            // Update job to mark as inactive and clear generatedAssets
            await supabase
              .from('video_processing_jobs')
              .update({ 
                is_active: false, 
                payload: updatedPayload,
                updated_at: new Date().toISOString() 
              })
              .eq('id', job.id);
          }
        }
      }

      return {
        success: true,
        data: {
          deletedMaster: options.deleteMaster,
          deletedThumbnails: options.deleteThumbnails,
        },
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in deleteVideoAssets:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Delete highlight assets (stitched and/or enhanced videos)
   */
  async deleteHighlightAssets(
    highlightId: string,
    options: {
      deleteStitched: boolean;
      deleteEnhanced: boolean;
    }
  ): Promise<ServiceResult<{ deletedStitched: boolean; deletedEnhanced: boolean }>> {
    try {
      // Get current session for auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Not authenticated' };
      }

      // Fetch highlight record
      const highlightResult = await videoHighlightsService.getHighlightById(highlightId);
      if (!highlightResult.success || !highlightResult.data) {
        return { success: false, error: highlightResult.error || 'Highlight not found' };
      }

      const highlight = highlightResult.data;
      const userId = session.user.id;

      // Build array of file paths to delete
      const filePaths: string[] = [];

      if (options.deleteStitched && highlight.output_storage_path) {
        filePaths.push(highlight.output_storage_path);
      }

      if (options.deleteEnhanced && highlight.enhanced_storage_path) {
        filePaths.push(highlight.enhanced_storage_path);
      }

      if (filePaths.length === 0) {
        return { success: false, error: 'No files found to delete' };
      }

      // orgId is optional - edge function can derive it from paths or skip membership check for highlights/tactics
      // We still try to get it for better security, but don't fail if we can't
      let orgId: string | undefined;
      try {
        const matchResult = await supabase
          .from('matches')
          .select('tournament_id, tournaments!inner(organisation_id)')
          .eq('id', highlight.match_id)
          .single();

        if (matchResult.data) {
          orgId = (matchResult.data as any).tournaments?.organisation_id;
        }
      } catch (err) {
        // If we can't get orgId, that's okay - edge function will handle it
        console.warn('Could not retrieve orgId for highlight deletion:', err);
      }

      // Call edge function to delete files from R2
      const { data: deleteResult, error: deleteError } = await supabase.functions.invoke(
        'delete-r2-files',
        {
          body: {
            ...(orgId && { orgId }), // Only include orgId if we have it
            userId,
            filePaths,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      ) as { data: DeleteR2FilesResponse | null; error: any };

      if (deleteError) {
        console.error('Error calling delete-r2-files:', deleteError);
        return { success: false, error: deleteError.message || 'Failed to delete files from R2' };
      }

      if (!deleteResult) {
        return { success: false, error: 'No response from delete-r2-files function' };
      }

      // Check if all deletions succeeded
      const allSucceeded = deleteResult.success && deleteResult.summary.failed === 0;
      if (!allSucceeded) {
        const failedPaths = deleteResult.results
          .filter((r) => !r.success)
          .map((r) => r.path);
        return {
          success: false,
          error: `Some files could not be deleted: ${failedPaths.join(', ')}`,
        };
      }

      // All R2 deletions succeeded, now update database
      const updates: Partial<VideoHighlight> = {};

      if (options.deleteStitched) {
        updates.output_storage_path = null;
        updates.output_url = null;
        updates.file_size_bytes = 0;
      }

      if (options.deleteEnhanced) {
        updates.enhanced_storage_path = null;
        updates.enhanced_url = null;
        updates.enhanced_file_size_bytes = 0;
      }

      // Determine if we should set is_active = false
      // First check if there are active social media uploads
      const { socialMediaService } = await import('../../../shared/services/socialMediaService');
      const hasUploadsResult = await socialMediaService.hasActiveUploads(highlightId, 'highlight');
      const hasActiveUploads = hasUploadsResult.success && hasUploadsResult.data === true;

      // Set to false only if both videos are being deleted AND no active uploads exist
      if (options.deleteStitched && options.deleteEnhanced) {
        // Don't set is_active = false if there are active uploads
        if (!hasActiveUploads) {
          updates.is_active = false;
        }
      } else {
        // Check if the other video still exists
        const hasStitched = options.deleteStitched
          ? false
          : Boolean(highlight.output_storage_path);
        const hasEnhanced = options.deleteEnhanced
          ? false
          : Boolean(highlight.enhanced_storage_path);

        // If neither exists AND no active uploads, set is_active = false
        if (!hasStitched && !hasEnhanced && !hasActiveUploads) {
          updates.is_active = false;
        }
      }

      // Update all fields directly via Supabase (updateHighlight filters by is_active=true, which fails for inactive highlights)
      const dbUpdatePayload: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
        updated_by: userId,
      };

      // Add storage field updates
      if (updates.output_storage_path !== undefined) {
        dbUpdatePayload.output_storage_path = updates.output_storage_path;
      }
      if (updates.output_url !== undefined) {
        dbUpdatePayload.output_url = updates.output_url;
      }
      if (updates.file_size_bytes !== undefined) {
        dbUpdatePayload.file_size_bytes = updates.file_size_bytes;
      }
      if (updates.enhanced_storage_path !== undefined) {
        dbUpdatePayload.enhanced_storage_path = updates.enhanced_storage_path;
      }
      if (updates.enhanced_url !== undefined) {
        dbUpdatePayload.enhanced_url = updates.enhanced_url;
      }
      if (updates.enhanced_file_size_bytes !== undefined) {
        dbUpdatePayload.enhanced_file_size_bytes = updates.enhanced_file_size_bytes;
      }
      if (updates.is_active !== undefined) {
        dbUpdatePayload.is_active = updates.is_active;
      }

      // Update highlight record directly (don't use updateHighlight as it filters by is_active=true)
      console.log(`[DELETE R2] Updating database for highlight ${highlightId}:`, dbUpdatePayload);
      const { data: updateData, error: updateError } = await supabase
        .from('video_highlights')
        .update(dbUpdatePayload)
        .eq('id', highlightId)
        .select();

      if (updateError) {
        console.error('Error updating highlight after deletion:', updateError);
        return {
          success: false,
          error: `Files deleted from R2 but database update failed: ${updateError.message}`,
        };
      }

      console.log(`[DELETE R2] Database update successful. Updated rows:`, updateData);

      return {
        success: true,
        data: {
          deletedStitched: options.deleteStitched,
          deletedEnhanced: options.deleteEnhanced,
        },
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in deleteHighlightAssets:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Delete tactics image asset
   */
  async deleteTacticsAsset(tacticsId: string): Promise<ServiceResult<void>> {
    try {
      // Get current session for auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Not authenticated' };
      }

      // Fetch tactics record
      const tacticsResult = await tacticsImagesService.getTacticsImageById(tacticsId);
      if (!tacticsResult.success || !tacticsResult.data) {
        return { success: false, error: tacticsResult.error || 'Tactics image not found' };
      }

      const tactics = tacticsResult.data;
      const userId = session.user.id;

      // Build array of file paths to delete
      const filePaths: string[] = [];

      if (tactics.image_storage_path) {
        filePaths.push(tactics.image_storage_path);
      }

      if (filePaths.length === 0) {
        return { success: false, error: 'No file found to delete' };
      }

      // orgId is optional - edge function can skip membership check for tactics
      // We still try to get it for better security, but don't fail if we can't
      let orgId: string | undefined;
      try {
        const tournamentResult = await supabase
          .from('tournaments')
          .select('organisation_id')
          .eq('id', tactics.tournament_id)
          .single();

        if (tournamentResult.data) {
          orgId = tournamentResult.data.organisation_id;
        }
      } catch (err) {
        // If we can't get orgId, that's okay - edge function will handle it
        console.warn('Could not retrieve orgId for tactics deletion:', err);
      }

      // Call edge function to delete file from R2
      const { data: deleteResult, error: deleteError } = await supabase.functions.invoke(
        'delete-r2-files',
        {
          body: {
            ...(orgId && { orgId }), // Only include orgId if we have it
            userId,
            filePaths,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      ) as { data: DeleteR2FilesResponse | null; error: any };

      if (deleteError) {
        console.error('Error calling delete-r2-files:', deleteError);
        return { success: false, error: deleteError.message || 'Failed to delete file from R2' };
      }

      if (!deleteResult) {
        return { success: false, error: 'No response from delete-r2-files function' };
      }

      // Check if deletion succeeded
      const allSucceeded = deleteResult.success && deleteResult.summary.failed === 0;
      if (!allSucceeded) {
        const failedPaths = deleteResult.results
          .filter((r) => !r.success)
          .map((r) => r.path);
        return {
          success: false,
          error: `File could not be deleted: ${failedPaths.join(', ')}`,
        };
      }

      // All R2 deletions succeeded, now update database
      // For tactics, we always set is_active = false (only one file)
      await supabase
        .from('tactics_images')
        .update({
          image_storage_path: null,
          image_url: null,
          file_size_bytes: 0,
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .eq('id', tacticsId);

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in deleteTacticsAsset:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },
};

